import { IconName } from '@taxbit-private/cosmic'

import { AirflowDagRun, AirflowDagRunState } from 'api/types/gcDagApiTypes'
import {
  PendingChanges,
  PendingChangesChild,
  PendingChangesResponse,
} from 'api/types/pendingChangesApiTypes'

/**
 * Minimum duration in seconds for a DAG run to be considered valid.
 * Runs shorter than this are likely automated triggers (airflow redeploys, org creation, etc.)
 * that skip actual processing steps.
 */
export const MIN_VALID_RUN_DURATION_SECONDS = 120

/**
 * Determines if a DAG run should be considered valid based on its duration.
 * Filters out "false" runs that complete in under 2 minutes.
 * Running or incomplete runs (no end_date) are always considered valid.
 */
export const isValidDagRun = (run: AirflowDagRun): boolean => {
  if (!run.start_date || !run.end_date) {
    // Running or incomplete runs are always valid
    return true
  }
  const durationSeconds =
    (new Date(run.end_date).getTime() - new Date(run.start_date).getTime()) / 1000
  return durationSeconds >= MIN_VALID_RUN_DURATION_SECONDS
}

/**
 * Finds the currently running DAG run, if any.
 */
export const getActiveDagRun = (
  dagRuns: AirflowDagRun[] | undefined
): AirflowDagRun | undefined => {
  if (!dagRuns) {
    return undefined
  }
  return dagRuns.find((run) => run.state === AirflowDagRunState.Running)
}

/**
 * Gets the most recent failed DAG run, filtered by minimum duration.
 */
export const getMostRecentFailedDagRun = (
  dagRuns: AirflowDagRun[] | undefined
): AirflowDagRun | undefined => {
  if (!dagRuns) {
    return undefined
  }
  const failedRuns = dagRuns.filter((run) => run.state === AirflowDagRunState.Failed)
  if (failedRuns.length === 0) {
    return undefined
  }
  return failedRuns.sort((a, b) => {
    const dateA = a.start_date ? new Date(a.start_date).getTime() : 0
    const dateB = b.start_date ? new Date(b.start_date).getTime() : 0
    return dateB - dateA
  })[0]
}

/**
 * Gets the last successful DAG run, filtered by minimum duration.
 */
export const getLastSuccessfulDagRun = (
  dagRuns: AirflowDagRun[] | undefined
): AirflowDagRun | undefined => {
  if (!dagRuns) {
    return undefined
  }
  const successfulRuns = dagRuns.filter(
    (run) => run.state === AirflowDagRunState.Success && isValidDagRun(run)
  )
  if (successfulRuns.length === 0) {
    return undefined
  }
  return successfulRuns.sort((a, b) => {
    const dateA = a.end_date
      ? new Date(a.end_date).getTime()
      : a.start_date
        ? new Date(a.start_date).getTime()
        : 0
    const dateB = b.end_date
      ? new Date(b.end_date).getTime()
      : b.start_date
        ? new Date(b.start_date).getTime()
        : 0
    return dateB - dateA
  })[0]
}

/**
 * Determines if a single pending change value is active.
 * - `null` → not pending (change type has not occurred)
 * - ISO 8601 string → pending if the change occurred after the last successful DAG run
 *   (or if there is no last successful DAG run)
 */
export const isPendingChangeActive = (
  value: string | null,
  lastDagRunTime: string | null | undefined
): boolean => {
  if (value === null) {
    return false
  }
  // Timestamp value: pending if no last successful run, or change is after the last run
  if (!lastDagRunTime) {
    return true
  }
  return new Date(value).getTime() > new Date(lastDagRunTime).getTime()
}

/**
 * Checks if there are any pending changes in the parent or children organizations.
 */
export const checkHasPendingChanges = (
  pendingChangesData: PendingChangesResponse | undefined
): boolean => {
  if (!pendingChangesData) {
    return false
  }

  const { last_successful_valid_dag_run_start_time, pending_changes } = pendingChangesData

  // Check parent's pending changes
  if (
    Object.values(pending_changes).some((value) =>
      isPendingChangeActive(value, last_successful_valid_dag_run_start_time)
    )
  ) {
    return true
  }

  // Check children's pending changes
  return pendingChangesData.children.some((child) =>
    Object.values(child.pending_changes).some((value) =>
      isPendingChangeActive(value, child.last_successful_valid_dag_run_start_time)
    )
  )
}

/**
 * Determines if the failed state should be shown.
 * Only shows failed state if the most recent failed run is more recent than the last successful run.
 */
export const checkIsFailedState = (
  mostRecentFailedDagRun: AirflowDagRun | undefined,
  lastSuccessfulDagRun: AirflowDagRun | undefined
): boolean => {
  if (!mostRecentFailedDagRun) {
    return false
  }

  const failedTime = mostRecentFailedDagRun.end_date
    ? new Date(mostRecentFailedDagRun.end_date).getTime()
    : mostRecentFailedDagRun.start_date
      ? new Date(mostRecentFailedDagRun.start_date).getTime()
      : 0

  const successTime = lastSuccessfulDagRun?.end_date
    ? new Date(lastSuccessfulDagRun.end_date).getTime()
    : lastSuccessfulDagRun?.start_date
      ? new Date(lastSuccessfulDagRun.start_date).getTime()
      : 0

  return failedTime > successTime
}

/**
 * Maps true progress (0–100+) to display progress and finalizing state.
 * 0–90%: show as-is. 90–<100%: scale to 90–95%. 100%+: cap at 95% and isFinalizing.
 */
export function computeDisplayProgress(trueProgress: number): {
  progress: number
  isFinalizing: boolean
} {
  if (trueProgress < 90) {
    return { progress: trueProgress, isFinalizing: false }
  }
  if (trueProgress < 100) {
    return { progress: 90 + (trueProgress - 90) * 0.5, isFinalizing: false }
  }
  return { progress: 95, isFinalizing: true }
}

export type ProgressData = {
  progress: number
  remainingSeconds: number
  elapsedSeconds: number
  isFinalizing: boolean
}

/**
 * Computes progress data for an in-progress DAG run from current time.
 * Returns null when not in progress or missing required data.
 * Used for synchronous first paint (no flash) and as fallback when state is stale.
 */
export function computeProgressData(
  statusState: string,
  pendingChangesData: PendingChangesResponse | undefined,
  activeDagRun: { start_date?: string | null } | undefined
): ProgressData | null {
  if (statusState !== 'in-progress' || !pendingChangesData || !activeDagRun?.start_date) {
    return null
  }
  const { average_dag_run_time_seconds } = pendingChangesData
  if (!average_dag_run_time_seconds) {
    return null
  }
  const startTime = new Date(activeDagRun.start_date).getTime()
  const now = Date.now()
  const elapsedSeconds = Math.floor((now - startTime) / 1000)
  const trueProgress = Math.max((elapsedSeconds / average_dag_run_time_seconds) * 100, 0)
  const { progress, isFinalizing } = computeDisplayProgress(trueProgress)
  const remainingSeconds = Math.max(average_dag_run_time_seconds - elapsedSeconds, 0)
  return { progress, remainingSeconds, elapsedSeconds, isFinalizing }
}

// --- Pending changes display utilities ---

type ChangeTypeDisplayProps = { title: string; iconName: IconName }

/**
 * Map of pending change types to their display title and icon.
 */
const changeTypeToPropsMap: Record<keyof PendingChanges, ChangeTypeDisplayProps> = {
  txn_update: { title: 'Transactions edited', iconName: 'table' },
  ingestion_update: { title: 'Sources synced', iconName: 'database' },
  mapping_upsert: { title: 'Chart of accounts edited', iconName: 'book-open' },
  mapping_delete: { title: 'Chart of accounts edited', iconName: 'book-open' },
  source_delete: { title: 'Sources deleted', iconName: 'database' },
  rule_upsert: { title: 'Rules modified', iconName: 'shuffle' },
  rule_delete: { title: 'Rules deleted', iconName: 'shuffle' },
  config_update: { title: 'Settings changed', iconName: 'settings' },
  pricing_update: { title: 'Pricing configured', iconName: 'settings' },
  pricing_refresh_request: { title: 'Pricing configured', iconName: 'settings' },
  spam_token_create: { title: 'Spam tokens added', iconName: 'settings' },
  spam_token_delete: { title: 'Spam tokens removed', iconName: 'settings' },
  spam_token_toggle_ingestion: { title: 'Spam detection toggled', iconName: 'settings' },
}

export type ProcessedPendingChange = {
  key: string
  title: string
  iconName: IconName
  timestamp: string
  legalEntityDisplayName?: string
}

/**
 * Filters active parent pending changes, deduplicates by display title
 * (keeping the most recent timestamp), and returns them unsorted.
 */
export const getActiveParentPendingChanges = (
  pendingChanges: PendingChanges | undefined,
  lastSuccessfulUpdate: string | null | undefined
): ProcessedPendingChange[] => {
  if (!pendingChanges) {
    return []
  }

  // Dedupe by display title, keeping the most recent timestamp per title.
  const latestByTitle = new Map<string, ProcessedPendingChange>()

  for (const changeType in pendingChanges) {
    const key = changeType as keyof PendingChanges
    const timestamp = pendingChanges[key]
    if (timestamp === null || !isPendingChangeActive(timestamp, lastSuccessfulUpdate)) {
      continue
    }

    const { title, iconName } = changeTypeToPropsMap[key]
    const existing = latestByTitle.get(title)

    if (!existing || new Date(timestamp).getTime() > new Date(existing.timestamp).getTime()) {
      latestByTitle.set(title, { key: changeType, title, iconName, timestamp })
    }
  }

  return [...latestByTitle.values()]
}

/**
 * Finds child orgs with active pending changes. Returns one entry per child
 * with only the most recent active change timestamp.
 */
export const getActiveChildOrgPendingChanges = (
  children: PendingChangesChild[] | undefined,
  childOrgDisplayNameBySlug: Map<string, string>
): ProcessedPendingChange[] => {
  if (!children) {
    return []
  }

  const result: ProcessedPendingChange[] = []

  for (const child of children) {
    let latestTimestamp: string | null = null

    for (const timestamp of Object.values(child.pending_changes)) {
      if (
        timestamp === null ||
        !isPendingChangeActive(timestamp, child.last_successful_valid_dag_run_start_time)
      ) {
        continue
      }
      if (
        latestTimestamp === null ||
        new Date(timestamp).getTime() > new Date(latestTimestamp).getTime()
      ) {
        latestTimestamp = timestamp
      }
    }

    if (latestTimestamp !== null) {
      const displayName = childOrgDisplayNameBySlug.get(child.slug)
      result.push({
        key: `child-org-${child.slug}-changes`,
        title: 'Changes detected',
        iconName: 'home',
        timestamp: latestTimestamp,
        legalEntityDisplayName: displayName,
      })
    }
  }

  return result
}
